import { createSlice } from "@reduxjs/toolkit";
import {
  getCalendarData,
  getTeamDetailData,
  regularisationApprovalList,
  regularisationHistory,
  submitRegularisation,
  updateRegularisationApproval,
  uploadRegularisationFileWithPresignedUrl,
  getLeaveDetails,
  submitLeave,
  uploadLeaveFileWithPresignedUrl,
  getLeaveStatusData,
  pendingLeaveApprovals,
  approvedLeaves,
  updateLeaveApprovals,
  getTeamDetailManagerData,
  getFilteredTeamData,
  getManagerReportDownload,
  getDashboardOverview,
  getLeaveCountBetweenDates,
  getTruIdStatus,
} from "./thunk";

const initialState = {
  islogged: false,
  calendarDataResponse: null,
  calendarDataResponseStatus: null,
  teamDetailDataResponse: null,
  teamDetailDataResponseStatus: null,
  regularisationPayload: {},
  regularisationFileUploadResponse: null,
  regularisationFileUploadResponseStatus: null,
  submitRegularisationResponse: null,
  submitRegularisationResponseStatus: null,
  regularisationHistoryResponse: null,
  regularisationHistoryResponseStatus: null,
  regularisationApprovalListResponse: null,
  regularisationApprovalListResponseStatus: null,
  updateRegularisationApprovalResponse: null,
  updateRegularisationApprovalResponseStatus: null,
  leaveDataResponse: null,
  leaveDataResponseStatus: null,
  leaveCountResponse: null,
  leaveCountResponseStatus: null,
  submitLeaveResponse: null,
  submitLeaveResponseStatus: null,
  leaveData: {},
  halfMode: false,
  leaveFileUploadResponse: null,
  leaveFileUploadResponseStatus: null,
  leaveDetailResponse: null,
  leaveDetailResponseStatus: null,
  pendingLeaveRequests: null,
  pendingLeaveRequestsStatus: null,
  approvedLeaveRequests: null,
  approvedLeaveRequestsStatus: null,
  updateApprovalResponse: null,
  updateApprovalResponseStatus: null,
  teamDetailManagerDataResponse: null,
  teamDetailManagerDataResponseStatus: null,
  getManagerReportDownloadResponse: null,
  getManagerReportDownloadResponseStatus: null,
  filteredTeamDataResponse: null,
  filteredTeamDataResponseStatus: null,
  dashboardOverviewResponse: null,
  dashboardOverviewResponseStatus: null,
  truIdStatusResponse: null,
  truIdStatusResponseStatus: null,
};

const attendanceSlice = createSlice({
  name: "attendance",
  initialState: initialState,
  reducers: {
    setIsLogged: (state, action) => {
      state.islogged = action.payload;
    },
    setInitialState: (state) => {
      return initialState;
    },
    resetRegularisationApprovals: (state) => {
      state.regularisationApprovalListResponse = null;
      state.regularisationApprovalListResponseStatus = null;
      state.updateRegularisationApprovalResponse = null;
      state.updateRegularisationApprovalResponseStatus = null;
    },
    setRegularisationPayload: (state, action) => {
      state.regularisationPayload = action.payload;
    },
    resetSubmitRegularisationResponse: (state) => {
      state.submitRegularisationResponse = null;
      state.submitRegularisationResponseStatus = null;
    },
    setLeaveData: (state, action) => {
      state.leaveData = action.payload;
    },
    setHalfMode: (state, action) => {
      state.halfMode = action.payload;
      state.leaveData = {
        ...state.leaveData,
        start: null,
        end: null,
        applied_days: null,
      };
    },
    setLeaveFile: (state, action) => {
      state.leaveData = {
        ...state.leaveData,
        file: action.payload,
      };
    },
    resetLeaveCount: (state, action) => {
      state.leaveCountResponse = null;
      state.leaveCountResponseStatus = null;
    },
    resetLeaveDetailStatus: (state) => {
      state.leaveDetailResponseStatus = null;
    },
    resetLeaveApprovals: (state) => {
      state.approvedLeaveRequests = null;
      state.approvedLeaveRequestsStatus = null;
      state.pendingLeaveRequests = null;
      state.pendingLeaveRequestsStatus = null;
    },
    resetFileUploadStatus: (state) => {
      state.regularisationFileUploadResponseStatus = null;
      state.regularisationFileUploadResponse = null;
    },
    resetSubmitLeaveResponse: (state) => {
      state.submitLeaveResponse = null;
      state.submitLeaveResponseStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCalendarData.pending, (state, action) => {
      state.calendarDataResponse = null;
      state.calendarDataResponseStatus = "PENDING";
    });
    builder.addCase(getCalendarData.rejected, (state, action) => {
      state.calendarDataResponse = action.payload;
      state.calendarDataResponseStatus = "ERROR";
    });
    builder.addCase(getCalendarData.fulfilled, (state, action) => {
      state.calendarDataResponse = action.payload;
      state.calendarDataResponseStatus = "SUCCESS";
    });

    builder.addCase(getTeamDetailData.pending, (state, action) => {
      state.teamDetailDataResponse = null;
      state.teamDetailDataResponseStatus = "PENDING";
    });
    builder.addCase(getTeamDetailData.rejected, (state, action) => {
      state.teamDetailDataResponse = action.payload;
      state.teamDetailDataResponseStatus = "ERROR";
    });
    builder.addCase(getTeamDetailData.fulfilled, (state, action) => {
      state.teamDetailDataResponse = action.payload;
      state.teamDetailDataResponseStatus = "SUCCESS";
    });

    builder.addCase(
      uploadRegularisationFileWithPresignedUrl.pending,
      (state, action) => {
        state.regularisationFileUploadResponse = null;
        state.regularisationFileUploadResponseStatus = "PENDING";
      },
    );
    builder.addCase(
      uploadRegularisationFileWithPresignedUrl.rejected,
      (state, action) => {
        state.regularisationFileUploadResponse = action.payload;
        state.regularisationFileUploadResponseStatus = "ERROR";
      },
    );
    builder.addCase(
      uploadRegularisationFileWithPresignedUrl.fulfilled,
      (state, action) => {
        state.regularisationFileUploadResponse = action.payload;
        state.regularisationFileUploadResponseStatus = "SUCCESS";
      },
    );

    builder.addCase(submitRegularisation.pending, (state, action) => {
      state.submitRegularisationResponse = null;
      state.submitRegularisationResponseStatus = "PENDING";
    });
    builder.addCase(submitRegularisation.rejected, (state, action) => {
      state.submitRegularisationResponse = action.payload;
      state.submitRegularisationResponseStatus = "ERROR";
    });
    builder.addCase(submitRegularisation.fulfilled, (state, action) => {
      state.submitRegularisationResponse = action.payload;
      state.submitRegularisationResponseStatus = "SUCCESS";
    });

    builder.addCase(regularisationHistory.pending, (state, action) => {
      state.regularisationHistoryResponse = null;
      state.regularisationHistoryResponseStatus = "PENDING";
    });
    builder.addCase(regularisationHistory.rejected, (state, action) => {
      state.regularisationHistoryResponse = action.payload;
      state.regularisationHistoryResponseStatus = "ERROR";
    });
    builder.addCase(regularisationHistory.fulfilled, (state, action) => {
      state.regularisationHistoryResponse = action.payload;
      state.regularisationHistoryResponseStatus = "SUCCESS";
    });

    builder.addCase(regularisationApprovalList.pending, (state, action) => {
      state.regularisationApprovalListResponse = null;
      state.regularisationApprovalListResponseStatus = "PENDING";
    });
    builder.addCase(regularisationApprovalList.rejected, (state, action) => {
      state.regularisationApprovalListResponse = action.payload;
      state.regularisationApprovalListResponseStatus = "ERROR";
    });
    builder.addCase(regularisationApprovalList.fulfilled, (state, action) => {
      state.regularisationApprovalListResponse = action.payload;
      state.regularisationApprovalListResponseStatus = "SUCCESS";
    });

    builder.addCase(updateRegularisationApproval.pending, (state, action) => {
      state.updateRegularisationApprovalResponse = null;
      state.updateRegularisationApprovalResponseStatus = "PENDING";
    });
    builder.addCase(updateRegularisationApproval.rejected, (state, action) => {
      state.updateRegularisationApprovalResponse = action.payload;
      state.updateRegularisationApprovalResponseStatus = "ERROR";
    });
    builder.addCase(updateRegularisationApproval.fulfilled, (state, action) => {
      state.updateRegularisationApprovalResponse = action.payload;
      state.updateRegularisationApprovalResponseStatus = "SUCCESS";
    });

    builder.addCase(getLeaveDetails.pending, (state, action) => {
      state.leaveDataResponse = null;
      state.leaveDataResponseStatus = "PENDING";
    });
    builder.addCase(getLeaveDetails.rejected, (state, action) => {
      state.leaveDataResponse = action.payload;
      state.leaveDataResponseStatus = "ERROR";
    });
    builder.addCase(getLeaveDetails.fulfilled, (state, action) => {
      state.leaveDataResponse = action.payload;
      state.leaveDataResponseStatus = "SUCCESS";
    });

    builder.addCase(getLeaveCountBetweenDates.pending, (state, action) => {
      state.leaveCountResponse = null;
      state.leaveCountResponseStatus = "PENDING";
    });
    builder.addCase(getLeaveCountBetweenDates.rejected, (state, action) => {
      state.leaveCountResponse = action.payload;
      state.leaveCountResponseStatus = "ERROR";
    });
    builder.addCase(getLeaveCountBetweenDates.fulfilled, (state, action) => {
      state.leaveCountResponse = action.payload;
      state.leaveCountResponseStatus = "SUCCESS";
    });

    builder.addCase(submitLeave.pending, (state, action) => {
      state.submitLeaveResponse = null;
      state.submitLeaveResponseStatus = "PENDING";
    });
    builder.addCase(submitLeave.rejected, (state, action) => {
      state.submitLeaveResponse = action.payload;
      state.submitLeaveResponseStatus = "ERROR";
    });
    builder.addCase(submitLeave.fulfilled, (state, action) => {
      state.submitLeaveResponse = action.payload;
      state.submitLeaveResponseStatus = "SUCCESS";
    });

    builder.addCase(
      uploadLeaveFileWithPresignedUrl.pending,
      (state, action) => {
        state.leaveFileUploadResponse = null;
        state.leaveFileUploadResponseStatus = "PENDING";
      },
    );
    builder.addCase(
      uploadLeaveFileWithPresignedUrl.rejected,
      (state, action) => {
        state.leaveFileUploadResponse = action.payload;
        state.leaveFileUploadResponseStatus = "ERROR";
      },
    );
    builder.addCase(
      uploadLeaveFileWithPresignedUrl.fulfilled,
      (state, action) => {
        state.leaveFileUploadResponse = action.payload;
        state.leaveFileUploadResponseStatus = "SUCCESS";
      },
    );

    builder.addCase(getLeaveStatusData.pending, (state, action) => {
      state.leaveDetailResponse = null;
      state.leaveDetailResponseStatus = "PENDING";
    });
    builder.addCase(getLeaveStatusData.rejected, (state, action) => {
      state.leaveDetailResponse = action.payload;
      state.leaveDetailResponseStatus = "ERROR";
    });
    builder.addCase(getLeaveStatusData.fulfilled, (state, action) => {
      state.leaveDetailResponse = action.payload;
      state.leaveDetailResponseStatus = "SUCCESS";
    });

    builder.addCase(pendingLeaveApprovals.pending, (state, action) => {
      state.pendingLeaveRequests = null;
      state.pendingLeaveRequestsStatus = "PENDING";
    });
    builder.addCase(pendingLeaveApprovals.rejected, (state, action) => {
      state.pendingLeaveRequests = action.payload;
      state.pendingLeaveRequestsStatus = "ERROR";
    });
    builder.addCase(pendingLeaveApprovals.fulfilled, (state, action) => {
      state.pendingLeaveRequests = action.payload;
      state.pendingLeaveRequestsStatus = "SUCCESS";
    });

    builder.addCase(approvedLeaves.pending, (state, action) => {
      state.approvedLeaveRequests = null;
      state.approvedLeaveRequestsStatus = "PENDING";
    });
    builder.addCase(approvedLeaves.rejected, (state, action) => {
      state.approvedLeaveRequests = action.payload;
      state.approvedLeaveRequestsStatus = "ERROR";
    });
    builder.addCase(approvedLeaves.fulfilled, (state, action) => {
      state.approvedLeaveRequests = action.payload;
      state.approvedLeaveRequestsStatus = "SUCCESS";
    });

    builder.addCase(updateLeaveApprovals.pending, (state, action) => {
      state.updateApprovalResponse = null;
      state.updateApprovalResponseStatus = "PENDING";
    });
    builder.addCase(updateLeaveApprovals.rejected, (state, action) => {
      state.updateApprovalResponse = action.payload;
      state.updateApprovalResponseStatus = "ERROR";
    });
    builder.addCase(updateLeaveApprovals.fulfilled, (state, action) => {
      state.updateApprovalResponse = action.payload;
      state.updateApprovalResponseStatus = "SUCCESS";
    });

    builder.addCase(getTeamDetailManagerData.pending, (state, action) => {
      state.teamDetailManagerDataResponse = null;
      state.teamDetailManagerDataResponseStatus = "PENDING";
    });
    builder.addCase(getTeamDetailManagerData.rejected, (state, action) => {
      state.teamDetailManagerDataResponse = action.payload;
      state.teamDetailManagerDataResponseStatus = "ERROR";
    });
    builder.addCase(getTeamDetailManagerData.fulfilled, (state, action) => {
      state.teamDetailManagerDataResponse = action.payload;
      state.teamDetailManagerDataResponseStatus = "SUCCESS";
    });

    builder.addCase(getFilteredTeamData.pending, (state, action) => {
      state.filteredTeamDataResponse = null;
      state.filteredTeamDataResponseStatus = "PENDING";
    });
    builder.addCase(getFilteredTeamData.rejected, (state, action) => {
      state.filteredTeamDataResponse = action.payload;
      state.filteredTeamDataResponseStatus = "ERROR";
    });
    builder.addCase(getFilteredTeamData.fulfilled, (state, action) => {
      state.filteredTeamDataResponse = action.payload;
      state.filteredTeamDataResponseStatus = "SUCCESS";
    });

    builder.addCase(getManagerReportDownload.pending, (state, action) => {
      state.getManagerReportDownloadResponse = null;
      state.getManagerReportDownloadResponseStatus = "PENDING";
    });
    builder.addCase(getManagerReportDownload.rejected, (state, action) => {
      state.getManagerReportDownloadResponse = action.payload;
      state.getManagerReportDownloadResponseStatus = "ERROR";
    });
    builder.addCase(getManagerReportDownload.fulfilled, (state, action) => {
      state.getManagerReportDownloadResponse = action.payload;
      state.getManagerReportDownloadResponseStatus = "SUCCESS";
    });

    builder.addCase(getDashboardOverview.pending, (state, action) => {
      state.dashboardOverviewResponse = null;
      state.dashboardOverviewResponseStatus = "PENDING";
    });
    builder.addCase(getDashboardOverview.rejected, (state, action) => {
      state.dashboardOverviewResponse = action.payload;
      state.dashboardOverviewResponseStatus = "ERROR";
    });
    builder.addCase(getDashboardOverview.fulfilled, (state, action) => {
      state.dashboardOverviewResponse = action.payload;
      state.dashboardOverviewResponseStatus = "SUCCESS";
    });

    builder.addCase(getTruIdStatus.pending, (state, action) => {
      state.truIdStatusResponse = null;
      state.truIdStatusResponseStatus = "PENDING";
    });
    builder.addCase(getTruIdStatus.rejected, (state, action) => {
      state.truIdStatusResponse = action.payload;
      state.truIdStatusResponseStatus = "ERROR";
    });
    builder.addCase(getTruIdStatus.fulfilled, (state, action) => {
      state.truIdStatusResponse = action.payload;
      state.truIdStatusResponseStatus = "SUCCESS";
    });
  },
});

export const attendanceActions = attendanceSlice.actions;

export default attendanceSlice.reducer;
